import * as React from 'react';
import { useState, useEffect } from 'react';
import '../styles/WorkQueueList.css';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton } from '@fluentui/react';
import { apiFetch } from '../lib/api-client';

export function WorkQueueList() {
    const [workQueueItems, setWorkQueueItems] = useState([]);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [dialogContent, setDialogContent] = useState('');
    const [dialogTitle, setDialogTitle] = useState('');

    useEffect(() => {
        fetchFailedWorkQueueItems();
    }, []);

    const fetchFailedWorkQueueItems = async () => {
        try {
            const response = await apiFetch('/api/failedtasks');
            if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data)) {
                    setWorkQueueItems(data);
                } else {
                    setWorkQueueItems([]);
                }
            } else {
                console.error('Failed to fetch work queue items');
            }
        } catch (error) {
            console.error('Error fetching work queue items:', error);
        }
    };

    const handleRetry = async (item) => {
        try {
            const response = await apiFetch('/api/requeuefailedtasks', {
                method: 'POST',
                body: JSON.stringify(item)
            });

            const responseBody = await response.text();

            if (response.ok) {
                setDialogTitle('Success');
                setDialogContent(`Successfully added the failed task back to the WorkQueue:\n
                Task: ${item.task}\n
                Target Date: ${new Date(item.targetDate).toLocaleString()}\n
                Service ID: ${item.serviceId}\n
                Environment ID: ${item.environmentId}\n
                Process ID: ${item.processId}\n
                Metric ID: ${item.metricId}\n
                Params: ${item.params || 'N/A'}`);
            } else {
                setDialogTitle('Error');
                setDialogContent(`Failed to add the task to the WorkQueue: ${item.task}. Error: ${responseBody}`);
            }
        } catch (error) {
            setDialogTitle('Error');
            setDialogContent(`Error in retrying the task: ${item.task}. Error: ${error.message}`);
        } finally {
            setIsDialogVisible(true);
            fetchFailedWorkQueueItems();
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getPriorityClass = (priority) => {
        return `priority-badge priority-${priority}`;
    };

    const getStatusBadge = (isActive, isFailed) => {
        if (isFailed) {
            return <span className="status-badge status-failed">Failed</span>;
        }
        if (isActive) {
            return <span className="status-badge status-active">Active</span>;
        }
        return <span className="status-badge status-inactive">Inactive</span>;
    };

    if (workQueueItems.length === 0) {
        return (
            <div className='WorkQueueList-container'>
                <h2>Failed WorkQueue Items</h2>
                <div className="empty-state">
                    <h3>No Failed Work Items Found</h3>
                    <p>All work items are running successfully!</p>
                </div>
            </div>
        );
    }

    return (
        <div className='WorkQueueList-container'>
            <Dialog
                hidden={!isDialogVisible}
                onDismiss={() => setIsDialogVisible(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: dialogTitle,
                    subText: dialogContent,
                }}
                modalProps={{
                    isBlocking: false,
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={() => setIsDialogVisible(false)} text="OK" />
                </DialogFooter>
            </Dialog>

            <h2>Failed WorkQueue Items ({workQueueItems.length})</h2>

            <div style={{ overflowX: 'auto' }}>
                <table className='WorkQueueList-table'>
                    <thead>
                        <tr>
                            <th>Task</th>
                            <th>Target Date</th>
                            <th>Service</th>
                            <th>Env</th>
                            <th>Process</th>
                            <th>Metric</th>
                            <th>Params</th>
                            <th>Priority</th>
                            <th>Status</th>
                            <th>Next Run Date</th>
                            <th>Work Item State</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {workQueueItems.map(item => (
                            <tr key={`${item.task}-${item.targetDate}-${item.serviceId}-${item.environmentId}-${item.processId}-${item.metricId}-${item.params}`}
                                className={item.failed ? 'failed-row' : ''}>
                                <td className="task-cell">{item.task}</td>
                                <td className="date-cell">{formatDate(item.targetDate)}</td>
                                <td><span className="id-cell">{item.serviceId}</span></td>
                                <td><span className="id-cell">{item.environmentId}</span></td>
                                <td><span className="id-cell">{item.processId}</span></td>
                                <td><span className="id-cell">{item.metricId}</span></td>
                                <td>{item.params || 'N/A'}</td>
                                <td><span className={getPriorityClass(item.priority)}>{item.priority}</span></td>
                                <td>{getStatusBadge(item.active, item.failed)}</td>
                                <td className="date-cell">{formatDate(item.nextRunDate)}</td>
                                <td>{item.workItemState || 'N/A'}</td>
                                <td>
                                    <div className="action-buttons">
                                        {item.failed && (
                                            <button className='retry-button' onClick={() => handleRetry(item)}>
                                                Requeue
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}